<template>
  <div class="grid-outer">
    <v-snackbar
      v-model="displayToastMessage"
      timeout="-1"
    >
      {{ toastText }}
    </v-snackbar>
    <div
      v-if="title"
      class="grid-title"
    >
      <p :class="title.class">
        {{ title.value }}
      </p>
      <p
        v-if="subTitle"
        :class="subTitle.class"
      >
        {{ subTitle.value }}
      </p>
      <p
        v-if="description"
        :class="description.class"
      >
        {{ description.value }}
      </p>
    </div>
    <div class="grid-actions">
      <v-btn
        class="clear-filters"
        size="sm"
        @click="clearFilters"
      >
        Clear filters
      </v-btn>

      <div class="csv-export">
        <v-btn
          size="sm"
          :disabled="exportLoading"
          @click="handleServerExport"
        >
          Export as CSV
        </v-btn>
      </div>
    </div>
    <div class="grid-wrapper">
      <ag-grid-vue
        :id="agGridID"
        :ref="agGridID"
        :style="gridStyles"
        dom-layout="normal"
        style="height: 100%;"
        class="ag-theme-alpine"
        :column-defs="columnDefs"
        row-model-type="serverSide"
        :server-side-infinite-scroll="true"
        :cache-block-size="cacheBlockSize"
        :max-concurrent-datasource-requests="4"
        :max-blocks-in-cache="15"
        :pagination-page-size="paginationPageSize"
        :pagination="map.isPagination"
        :components="frameworkComponents"
        :grid-options="gridOptions"
        :loading-cell-renderer-selector="handleCellRenderer"
        :modules="modules"
        :context="context"
        :overlay-no-rows-template="overlayNoRowsTemplate"
        @bodyScrollEnd="bodyScrollEnd"
        @grid-ready="onGridReady"
        @filter-changed="handleFilterChange"
        @column-resized="saveGridColumnState"
        @column-visible="saveGridColumnState"
        @column-moved="saveGridColumnState"
        @column-pinned="saveGridColumnState"
      />
    </div>
  </div>
</template>

<script>
import { AgGridVue } from '@ag-grid-community/vue'
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel'
import { MultiFilterModule } from '@ag-grid-enterprise/multi-filter'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { SetFilterModule } from '@ag-grid-enterprise/set-filter'

import { v4 as uuidv4 } from 'uuid'
import requests from './mixins/requests'
import gridSetup from './mixins/gridSetup'
import noMoreMixin from './mixins/noMore.mixin'

export default {
  name: 'aspediacrm-grid',
  components: {
    AgGridVue
  },
  mixins: [requests, noMoreMixin, gridSetup],
  props: {
    map: {
      type: Object,
      required: true
    },
    globalContext: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      frameworkComponents: {
        emailRenderer: 'emailRenderer',
        addressRenderer: 'addressRenderer',
        paragraphRenderer: 'paragraphRenderer',
        actionsRenderer: 'actionsRenderer',
        numberRenderer: 'numberRenderer',
        phoneRenderer: 'phoneRenderer',
        dateRenderer: 'dateRenderer',
        textRenderer: 'textRenderer',
        booleanRenderer: 'booleanRenderer',
        enumRenderer: 'enumRenderer',
        loadingCell: 'loadingCellRenderer'
      },
      overlayNoRowsTemplate: null,
      modules: [
        ServerSideRowModelModule,
        ColumnsToolPanelModule,
        MenuModule,
        SetFilterModule,
        MultiFilterModule
      ],
      context: { componentParent: this },
      gridOptionsDefaults: {
        suppressPropertyNamesCheck: true,
        suppressCellFocus: true,
        suppressMenuHide: true,
        defaultColDef: {
          resizable: true
        }
      },
      exportLoading: false,
      scrollNoMore: false,
      gridAPI: null,
      paginationPageSize: this.map.fetchStrategy.limit,
      columnDefs: this.map.fields.columnHeader,
      cacheBlockSize: this.map.fetchStrategy.limit,
      start: 0,
      rowCount: -1,
      filters: [],
      sortingList: []
    }
  },
  computed: {
    ToastID: function() {
      return `${this.agGridID}-toast`
    },
    agGridID: function() {
      return `aggrid-${uuidv4()}`
    },
    gridOptions: function() {
      return { ...this.gridOptionsDefaults, ...this.map.gridOptions }
    },
    gridStyles: function() {
      return Object.entries({
        height: '500px',
        ...this.map.gridStyles
      })
        .map(([k, v]) => `${k}: ${v}`)
        .join('; ')
    }
  }
}
</script>

<style lang="scss" scoped>

.grid-wrapper {
  width: 100%;
}

.csv-export {
  display: inline-block;

  .export-message {
    font-size: 12px;
  }
}

.ag-theme-alpine {
  margin-top: 10px;
}

.btn {
  margin-right: 10px;
}

</style>

<style lang="scss">

.ag-cell-overflow-allowed {
  overflow: visible !important;
}

.ag-row {
  z-index: 0;
}

.ag-row.ag-row-focus {
  z-index: 1;
}

.ag-root.ag-layout-auto-height,
.ag-body-viewport.ag-layout-auto-height,
.ag-body-viewport-wrapper.ag-layout-auto-height {
  overflow: visible;
}

.ag-center-cols-container {
  position: relative;

  #aggrid-no-more-top {
    height: 42px;

    span {
      position: fixed;
      left: 50%;
      transform: translateX(-50%);
      top: 15px;
    }
  }

  #aggrid-no-more {
    height: 42px;
    position: fixed;
    width: 100%;

    span {
      position: fixed;
      left: 50%;
      transform: translateX(-50%);
    }
  }
}
</style>
